"use client";

import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { track } from "@vercel/analytics";
import { Check, Camera, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { cdnImageUrlFromPath } from "@/lib/design-search/cdn-image";
import { DesignSearchSheet } from "@/components/design-search-sheet";
import { requestFullscreen } from "@/lib/fullscreen";
import { PromptPopup } from "@/components/prompt-popup";
import { HuntImageThumbnail } from "@/components/hunt-image-thumbnail";

interface HuntItemCardProps {
  item: {
    id: string;
    hunt_id?: string;
    name: string;
    description: string | null;
  };
  index: number;
  isCompleted: boolean;
  patentId?: string;
  inputImageUrl?: string | null;
  patentCdnPath?: string | null;
  huntId?: string;
  onComplete: (itemId: string, payload: { patentId: string; patentCdnPath: string; capturedImageDataUrl: string }) => Promise<void>;
}

export function HuntItemCard({ item, index, isCompleted, patentId, inputImageUrl, patentCdnPath, huntId, onComplete }: HuntItemCardProps) {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const handlePatentSelected = async (payload: { patentId: string; patentCdnPath: string; capturedImageDataUrl: string }) => {
    setIsSubmitting(true);
    try {
      await onComplete(item.id, payload);
      setShowPrompt(true);
    } catch {
      // Error toast is shown by the parent handleComplete
    } finally {
      setIsSubmitting(false);
    }
  };

  const patentImageUrl = patentCdnPath ? cdnImageUrlFromPath(patentCdnPath) : null;

  return (
    <>
      <Card className={cn(
        "rounded-2xl shadow-sm transition-all duration-200",
        isCompleted && "border-green-500 bg-green-50 dark:bg-green-950/20"
      )}>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {index}
            </span>
            <CardTitle className="min-w-0 flex-1 text-lg leading-tight">{item.name}</CardTitle>
            {isCompleted && (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-500 shadow-sm">
                <Check className="h-5 w-5 text-white" />
              </div>
            )}
          </div>
          {item.description && (
            <CardDescription className="mt-1 text-sm leading-relaxed">{item.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="pt-0">
          {isCompleted ? (
            (inputImageUrl || patentImageUrl) ? (
              <div className="flex items-center gap-2">
                {inputImageUrl ? (
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                    <HuntImageThumbnail src={inputImageUrl} alt="Your search photo" />
                    <span className="text-xs font-medium text-muted-foreground">Your photo</span>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1" />
                )}

                <div className="flex shrink-0 flex-col items-center gap-0.5 px-1">
                  <ArrowRight className="h-5 w-5 text-green-500" strokeWidth={2.5} />
                </div>

                {patentImageUrl ? (
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                    <HuntImageThumbnail src={patentImageUrl} alt={`Patent ${patentId}`} />
                    <span className="truncate text-xs font-bold tabular-nums text-green-600 dark:text-green-400">
                      {patentId}
                    </span>
                  </div>
                ) : (
                  <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
                    <p className="text-base font-bold text-green-600 dark:text-green-400">{patentId}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-base text-green-600 dark:text-green-400">
                Found <span className="font-bold">{patentId}</span>
              </p>
            )
          ) : (
            <Button
              onClick={() => {
                track("hunt_item_clicked", { item: item.name, index });
                void requestFullscreen();
                setIsSearchOpen(true);
              }}
              disabled={isSubmitting}
              variant="outline"
              className="h-12 w-full rounded-xl text-base font-medium"
            >
              <Camera className="mr-2 size-7 shrink-0" strokeWidth={1.5} />
              {isSubmitting ? "Submitting..." : "Search for a Patent"}
            </Button>
          )}
        </CardContent>
      </Card>

      <DesignSearchSheet
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        itemName={item.name}
        itemIndex={index}
        onSelect={handlePatentSelected}
      />

      <PromptPopup
        open={showPrompt}
        onOpenChange={setShowPrompt}
        huntId={huntId || item.hunt_id}
      />
    </>
  );
}
